type OpeningHours = { [day: string]: string };

export type TCardFormatStores = {
  division: string;
  floor: string;
  branchName: string;
  fullAddress: string;
  postcode: string;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  sunday: string;
  browsingHours: string;
  bankHoliday: string;
  isBureau: boolean;
  isClickAndCollect: boolean;
};

export const days = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export function formatTimeRange(timeRange: string): string {
  return timeRange?.replace("-", " - ");
}

export function formatOpeningHours(
  hours: OpeningHours,
  hasBankHoliday: boolean
): string[] {
  const dayNames = {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
  };

  let result: string[] = [];
  let currentGroup: string[] = [];
  let currentSchedule = "";

  for (let i = 0; i < days.length; i++) {
    const day = days[i];
    const schedule = formatTimeRange(hours[day]);

    const isWeekend = day === "saturday" || day === "sunday";

    if (!isWeekend && schedule === currentSchedule) {
      currentGroup.push(day);
    } else {
      if (currentGroup.length > 0) {
        result.push(
          buildGroupLabel(
            currentGroup,
            currentSchedule,
            dayNames,
            hasBankHoliday
          )
        );
      }

      currentGroup = [day];
      currentSchedule = schedule;
    }
  }

  if (currentGroup.length > 0) {
    result.push(
      buildGroupLabel(currentGroup, currentSchedule, dayNames, hasBankHoliday)
    );
  }

  return result;
}

function buildGroupLabel(
  group: string[],
  schedule: string,
  dayNames: { [key: string]: string },
  hasBankHoliday: boolean
): string {
  const groupDayLabels = group.map((day) =>
    hasBankHoliday && day !== "saturday" && day !== "sunday"
      ? `${dayNames[day]}`
      : dayNames[day]
  );

  let label = "";

  if (groupDayLabels.length > 1) {
    const rangeLabel = `${groupDayLabels[0]} - ${
      groupDayLabels[groupDayLabels.length - 1]
    }`;
    label = hasBankHoliday ? `${rangeLabel}*` : rangeLabel;
  } else {
    const singleLabel = groupDayLabels[0];
    const isWeekend = singleLabel === "Saturday" || singleLabel === "Sunday";
    label = hasBankHoliday && !isWeekend ? `${singleLabel}*` : singleLabel;
  }

  return `${label}: ${schedule}`;
}

export const extractFloorFromMessage = (
  message: string
): string | undefined => {
  if (!message) return undefined;

  const floorRegex =
    /(\b(?:upper\s+ground|lower\s+ground|ground|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth)\b)\s*floor|\b(mezzanine)\b/i;

  const match = message.match(floorRegex);

  if (match) {
    const ordinalToNumber: Record<string, string> = {
      "upper ground": "Upper ground",
      "lower ground": "Lower ground",
      mezzanine: "Mezzanine",
      ground: "Ground",
      first: "1st",
      second: "2nd",
      third: "3rd",
      fourth: "4th",
      fifth: "5th",
      sixth: "6th",
      seventh: "7th",
      eighth: "8th",
      ninth: "9th",
      tenth: "10th",
    };

    const ordinal = (match[1] || match[2]).toLowerCase();
    return ordinalToNumber[ordinal] || "TBA";
  }

  return undefined;
};
